import { classnames } from '@aibees/shared/utils';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { memo } from 'react';
import { DialogTitle } from '../dialog-title';
import { Icon } from '../icon';
import { useVideoPlayer } from './hooks';
import * as Styled from './styles';
import * as SX from './sx';
import type { VideoModalProps as Props } from './types';

const DEFAULT_LOAD_TIMEOUT_MS = 12_000;

export const VideoModal = memo(
  ({
    className,
    state,
    videoUrl,
    title,
    titleIcon,
    autoplay = true,
    loadTimeoutMs = DEFAULT_LOAD_TIMEOUT_MS,
    maxWidth = 'md',
    paperSx,
    onClose,
    actions,
    description,
  }: Props) => {
    const { resolved, phase, handleLoad, handleError } = useVideoPlayer({
      videoUrl,
      isOpen: state.value,
      autoplay,
      loadTimeoutMs,
    });

    const accessibleTitle =
      typeof title === 'string' && title ? title : 'Video player';

    const handleOpenInNewTab = () => {
      window.open(videoUrl, '_blank', 'noopener,noreferrer');
    };

    const handleClose = () => {
      state.onFalse();
      onClose?.();
    };

    return (
      <Dialog
        className={classnames('VideoModal', className)}
        open={state.value}
        maxWidth={maxWidth}
        PaperProps={{ sx: { width: '100%', ...paperSx } }}
      >
        <DialogTitle title={title} icon={titleIcon} onClose={handleClose} />
        <DialogContent>
          {description}
          <Styled.Frame sx={SX.aspectFrame}>
            {state.value && resolved && phase !== 'error' && (
              <Styled.Player
                key={resolved.embedUrl}
                src={resolved.embedUrl}
                title={accessibleTitle}
                allow={resolved.provider.iframeAllow}
                sandbox={resolved.provider.iframeSandbox}
                referrerPolicy="strict-origin-when-cross-origin"
                loading="lazy"
                allowFullScreen
                onLoad={handleLoad}
                onError={handleError}
                sx={SX.iframe}
              />
            )}

            {state.value && phase === 'loading' && (
              <Box sx={SX.overlay}>
                <CircularProgress size={32} sx={SX.spinner} />
              </Box>
            )}

            {phase === 'error' && (
              <Stack sx={SX.errorOverlay}>
                <Icon name="warning-amber" size={40} color="grey.500" />
                <Stack alignItems="center" gap={0.5}>
                  <Typography variant="body2" color="grey.800">
                    Video couldn&apos;t load
                  </Typography>
                  <Typography variant="body3" color="grey.600">
                    The embed may be blocked. You can open it in a new tab.
                  </Typography>
                </Stack>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleOpenInNewTab}
                  startIcon={<Icon name="open-in-new" size={16} />}
                >
                  Open in new tab
                </Button>
              </Stack>
            )}
          </Styled.Frame>
        </DialogContent>
        {!!actions && <DialogActions>{actions}</DialogActions>}
      </Dialog>
    );
  }
);

VideoModal.displayName = 'VideoModal';
